import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { Tag, Plus, Edit2, Trash2, Save, X, AlertCircle, CheckCircle } from 'lucide-react';

interface Discount {
  id: string;
  code: string;
  percentage: number;
  description: string;
  isActive: boolean;
  expirationDate?: any;
  usageCount?: number;
  createdAt?: any;
  updatedAt?: any;
}

export default function SalepageDiscounts() {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    percentage: 10,
    description: '',
    isActive: true,
    expirationDate: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    try {
      const discountsSnap = await getDocs(collection(db, 'salePageDiscounts'));
      const discountsData = discountsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Discount[];
      setDiscounts(discountsData.sort((a, b) => b.createdAt?.toDate()?.getTime() - a.createdAt?.toDate()?.getTime()));
      setLoading(false);
    } catch (error) {
      console.error('Error loading discounts:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const code = formData.code.trim().toUpperCase();
      
      if (!code) {
        setError('กรุณากรอกรหัสส่วนลด');
        setSaving(false);
        return;
      }

      if (formData.percentage < 1 || formData.percentage > 100) {
        setError('เปอร์เซ็นต์ส่วนลดต้องอยู่ระหว่าง 1-100');
        setSaving(false);
        return;
      }

      const existingDiscount = discounts.find(d => d.code === code && d.id !== editingDiscount?.id);
      if (existingDiscount) {
        setError('รหัสส่วนลดนี้มีอยู่แล้ว');
        setSaving(false);
        return;
      }

      const discountData: any = {
        code,
        percentage: formData.percentage,
        description: formData.description,
        isActive: formData.isActive,
        updatedAt: serverTimestamp()
      };

      if (formData.expirationDate) {
        discountData.expirationDate = new Date(formData.expirationDate);
      } else {
        discountData.expirationDate = null;
      }

      if (editingDiscount) {
        await updateDoc(doc(db, 'salePageDiscounts', editingDiscount.id), discountData);
      } else {
        discountData.usageCount = 0;
        discountData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'salePageDiscounts'), discountData);
      }

      await loadDiscounts();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving discount:', error);
      setError('เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (discountId: string) => {
    if (!confirm('คุณต้องการลบรหัสส่วนลดนี้หรือไม่?')) return;

    try {
      await deleteDoc(doc(db, 'salePageDiscounts', discountId));
      await loadDiscounts();
    } catch (error) {
      console.error('Error deleting discount:', error);
      alert('เกิดข้อผิดพลาดในการลบ');
    }
  };

  const handleEdit = (discount: Discount) => {
    setEditingDiscount(discount);
    const expirationDateStr = discount.expirationDate 
      ? new Date(discount.expirationDate.toDate()).toISOString().split('T')[0]
      : '';
    setFormData({
      code: discount.code,
      percentage: discount.percentage,
      description: discount.description,
      isActive: discount.isActive,
      expirationDate: expirationDateStr
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingDiscount(null);
    setFormData({
      code: '',
      percentage: 10,
      description: '',
      isActive: true,
      expirationDate: ''
    });
    setError('');
  };

  const toggleActive = async (discount: Discount) => {
    try {
      await updateDoc(doc(db, 'salePageDiscounts', discount.id), {
        isActive: !discount.isActive,
        updatedAt: serverTimestamp()
      });
      await loadDiscounts();
    } catch (error) {
      console.error('Error toggling discount:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Tag className="w-7 h-7 text-emerald-600" />
            จัดการส่วนลด
          </h2>
          <p className="text-gray-600 mt-1">สร้างและจัดการรหัสส่วนลดสำหรับลูกค้า Salepage</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-cyan-700 transition-all shadow-lg"
        >
          <Plus className="w-5 h-5" />
          เพิ่มส่วนลด
        </button>
      </div>

      <div className="grid gap-4">
        {discounts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">ยังไม่มีรหัสส่วนลด</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-emerald-600 hover:text-emerald-700 font-semibold"
            >
              เพิ่มรหัสส่วนลดแรก
            </button>
          </div>
        ) : (
          discounts.map(discount => (
            <div
              key={discount.id}
              className={`bg-white rounded-2xl shadow-lg p-6 border-2 transition-all ${
                discount.isActive ? 'border-emerald-200' : 'border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{discount.code}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      discount.isActive 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {discount.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </span>
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-700">
                      ส่วนลด {discount.percentage}%
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{discount.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>ใช้งานแล้ว: {discount.usageCount || 0} ครั้ง</span>
                    {discount.expirationDate && (
                      <span className={`font-semibold ${
                        new Date(discount.expirationDate.toDate()) < new Date() 
                          ? 'text-red-600' 
                          : 'text-blue-600'
                      }`}>
                        หมดอายุ: {discount.expirationDate.toDate().toLocaleDateString('th-TH')}
                      </span>
                    )}
                    {!discount.expirationDate && (
                      <span className="text-green-600 font-semibold">ไม่มีวันหมดอายุ</span>
                    )}
                    {discount.createdAt && (
                      <span>สร้างเมื่อ: {discount.createdAt.toDate().toLocaleDateString('th-TH')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(discount)}
                    className={`p-2 rounded-lg transition-colors ${
                      discount.isActive
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={discount.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleEdit(discount)}
                    className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(discount.id)}
                    className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingDiscount ? 'แก้ไขส่วนลด' : 'เพิ่มส่วนลดใหม่'}
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  รหัสส่วนลด
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="เช่น SAVE20"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none font-semibold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  เปอร์เซ็นต์ส่วนลด (%)
                </label>
                <input
                  type="number"
                  value={formData.percentage}
                  onChange={(e) => setFormData({ ...formData, percentage: parseInt(e.target.value) || 0 })}
                  min="1"
                  max="100"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  คำอธิบาย
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="เช่น ส่วนลด 20% สำหรับลูกค้าใหม่"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  วันหมดอายุ (ไม่บังคับ)
                </label>
                <input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">ถ้าไม่ระบุ รหัสส่วนลดจะไม่มีวันหมดอายุ</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  เปิดใช้งานทันที
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-cyan-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังบันทึก...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      บันทึก
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
